interface UploadPortalResponse {
  ok: boolean;
  status: number;
  statusText: string;
  successfulFiles?: { sopInstanceUID: string; fileName?: string }[];
  failedFiles?: { sopInstanceUID: string; error: string; code: number }[];
}


export const uploadPortal = async (
  token: any,
  blob: any,
  boundary: any
): Promise<UploadPortalResponse> => {
  const authToken = token;
  const { gcpBaseURL, dicomModifyServiceURL } = (window as any).data.auth;

  const useServerSideDeidentification =
    dicomModifyServiceURL &&
    (window as any).data.dicomTags?.serverSideModification !== false;

  // Debug logging
  console.log('Upload Portal Debug:', {
    dicomModifyServiceURL,
    serverSideModification: (window as any).data.dicomTags?.serverSideModification,
    useServerSideDeidentification,
    blobSize: blob?.size,
    blobType: blob?.type
  });

  if (useServerSideDeidentification) {
    console.log('Entering de-identification path...');
    try {
      // Use unified upload method that automatically selects the best strategy
      return await uploadDicomFile(blob, boundary, dicomModifyServiceURL, authToken);
    } catch (error: any) {
      console.error('Error in de-identification path:', error);
      return {
        ok: false,
        status: 0,
        statusText: error.message || "Network error",
        successfulFiles: [],
        failedFiles: [],
      };
    }
  } else {
    console.log('Skipping de-identification, using direct upload path');
    const res = await fetch(`${gcpBaseURL}`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    }).then(async (res) => {
      const data = await res.json();
      if (res.status === 200) {
        const response = await fetch(`${data.url}`, {
          method: "POST",
          body: blob,
          headers: {
            Authorization: `Bearer ${data.gcp_token}`,
            "Content-Type": `multipart/related; type="application/dicom"; boundary=${boundary}`,
          },
        });
        return response;
      }
    });

    if (res && res.ok) {
      try {
        const responseData = await res.json();
        const successfulFiles: { sopInstanceUID: string; fileName?: string }[] = [];
        const failedFiles: {
          sopInstanceUID: string;
          error: string;
          code: number;
        }[] = [];

        if (responseData["00081199"]?.Value) {
          responseData["00081199"].Value.forEach((item: any) => {
            const sopInstanceUID = item["00081155"]?.Value?.[0] || "";
            successfulFiles.push({ sopInstanceUID, fileName: "Unknown file" });
          });
        }

        if (responseData["00081198"]?.Value) {
          responseData["00081198"].Value.forEach((item: any) => {
            const sopInstanceUID = item["00081155"]?.Value?.[0] || "";
            const error = item["00090097"]?.Value?.[0] || "Unknown error";
            const code = item["00081197"]?.Value?.[0] || 0;
            failedFiles.push({ sopInstanceUID, error, code });
          });
        }

        return {
          ok: true,
          status: res.status,
          statusText: res.statusText,
          successfulFiles,
          failedFiles,
        };
      } catch (e) {
        return {
          ok: res.ok,
          status: res.status,
          statusText: res.statusText,
          successfulFiles: [],
          failedFiles: [],
        };
      }
    }

    return {
      ok: false,
      status: res?.status || 0,
      statusText: res?.statusText || "Upload failed",
      successfulFiles: [],
      failedFiles: [],
    };
  }
};

/**
 * Chunked Upload for all files >= 20MB
 * Splits file into chunks and uploads sequentially
 * This method can handle files of any size by chunking them
 * 
 * Fix for ERR_BLOB_REFERENCED_BLOB_BROKEN: Read chunk data into ArrayBuffer
 * before creating FormData to ensure data is available even if the original
 * blob reference becomes invalid.
 */
const uploadInChunks = async (
  blob: Blob,
  boundary: string,
  serviceURL: string,
  authToken: string,
  chunkSize: number
): Promise<Response> => {
  const totalChunks = Math.ceil(blob.size / chunkSize);
  const uploadId = `upload_${Date.now()}_${Math.random().toString(36).substring(2)}`;
  
  console.log(`Uploading ${totalChunks} chunks (${(blob.size / 1024 / 1024).toFixed(2)}MB total)`);
  
  // Extract base URL (remove route path if present)
  const baseURL = serviceURL.replace('/modify-and-upload-dicom', '');
  
  // Upload chunks sequentially with retry logic
  for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
    const start = chunkIndex * chunkSize;
    const end = Math.min(start + chunkSize, blob.size);
    const isLastChunk = chunkIndex === totalChunks - 1;
    
    // Read chunk data into ArrayBuffer to avoid blob reference issues
    // This ensures we have the actual data even if the original blob becomes invalid
    let chunkData: ArrayBuffer | null = null;
    let readRetryCount = 0;
    const maxReadRetries = 3;
    
    while (readRetryCount <= maxReadRetries) {
      try {
        const chunkBlob = blob.slice(start, end);
        chunkData = await chunkBlob.arrayBuffer();
        break; // Success, exit retry loop
      } catch (error: any) {
        readRetryCount++;
        if (readRetryCount > maxReadRetries) {
          throw new Error(
            `Failed to read chunk ${chunkIndex + 1}/${totalChunks} after ${maxReadRetries} retries: ` +
            `${error?.message || 'Blob reference broken. The file may be too large for browser processing.'}`
          );
        }
        console.warn(`Retry ${readRetryCount}/${maxReadRetries} reading chunk ${chunkIndex + 1}...`);
        // Wait before retry (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, readRetryCount) * 100));
      }
    }
    
    // TypeScript guard: chunkData should never be null here, but we check to satisfy the compiler
    if (!chunkData) {
      throw new Error(`Failed to read chunk ${chunkIndex + 1}/${totalChunks}: chunkData is null`);
    }
    
    const chunkSizeMB = (chunkData.byteLength / 1024 / 1024).toFixed(2);
    console.log(`Uploading chunk ${chunkIndex + 1}/${totalChunks} (${chunkSizeMB}MB)`);
    
    // Create a new Blob from the ArrayBuffer to ensure it's valid
    const chunkBlob = new Blob([chunkData]);
    
    // Upload with retry logic
    let uploadSuccess = false;
    let uploadRetryCount = 0;
    const maxUploadRetries = 3;
    
    while (uploadRetryCount <= maxUploadRetries && !uploadSuccess) {
      try {
        const formData = new FormData();
        formData.append('chunk', chunkBlob, `chunk-${chunkIndex}.bin`);
        formData.append('boundary', boundary);
        formData.append('uploadId', uploadId);
        formData.append('chunkIndex', chunkIndex.toString());
        formData.append('totalChunks', totalChunks.toString());
        formData.append('isLastChunk', isLastChunk.toString());
        formData.append('totalSize', blob.size.toString());
        
        const chunkResponse = await fetch(`${baseURL}/chunked-upload`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
          body: formData,
        });
        
        if (!chunkResponse.ok) {
          const errorText = await chunkResponse.text().catch(() => chunkResponse.statusText);
          throw new Error(`HTTP ${chunkResponse.status}: ${errorText}`);
        }
        
        uploadSuccess = true;
        
        // Check if all chunks are received (even if not the last chunk in the loop)
        const responseData = await chunkResponse.json().catch(() => ({}));
        if (responseData.ready_for_finalize || responseData.is_complete) {
          console.log(`All chunks received. Calling finalize endpoint...`);
          // Break out of loop and call finalize
          break;
        }
      } catch (error: any) {
        uploadRetryCount++;
        if (uploadRetryCount > maxUploadRetries) {
          const errorMessage = error?.message || 'Unknown error';
          throw new Error(
            `Chunk ${chunkIndex + 1}/${totalChunks} upload failed after ${maxUploadRetries} retries: ${errorMessage}`
          );
        }
        console.warn(`Retry ${uploadRetryCount}/${maxUploadRetries} uploading chunk ${chunkIndex + 1}...`);
        // Wait before retry (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, uploadRetryCount) * 100));
      }
    }
  }
  
  // Always call finalize endpoint after all chunks are uploaded
  // This ensures processing happens asynchronously and avoids Cloud Run timeouts
  console.log(`Requesting final processing for upload ${uploadId}...`);
  const finalResponse = await fetch(`${baseURL}/chunked-upload-finalize`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${authToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ uploadId, boundary }),
  });
  
  if (!finalResponse.ok) {
    let errorText = await finalResponse.text().catch(() => finalResponse.statusText);
    
    // Try to parse JSON error response
    try {
      const errorData = JSON.parse(errorText);
      if (errorData.statusText) {
        errorText = errorData.statusText;
      }
    } catch (e) {
      // If not JSON, use the text as is
    }
    
    // Create user-friendly error message based on status code
    let userFriendlyMessage = '';
    if (finalResponse.status === 409) {
      userFriendlyMessage = 'This study already exists.';
    } else if (finalResponse.status === 413) {
      userFriendlyMessage = 'File is too large for upload.';
    } else if (finalResponse.status === 400) {
      userFriendlyMessage = 'Invalid upload request.';
    } else if (finalResponse.status === 401 || finalResponse.status === 403) {
      userFriendlyMessage = 'Authentication failed. Please refresh and try again.';
    } else if (finalResponse.status >= 500) {
      userFriendlyMessage = 'Server error occurred. Please try again later.';
    } else {
      userFriendlyMessage = `Upload failed: ${errorText || finalResponse.statusText || 'Unknown error'}`;
    }
    
    throw new Error(userFriendlyMessage);
  }
  
  return finalResponse;
};

/**
 * Unified upload method that automatically selects the best strategy based on file size:
 * - < 20MB: Direct FormData upload (synchronous)
 * - >= 20MB: Chunked upload (synchronous) - handles all large files
 */
const uploadDicomFile = async (
  blob: Blob,
  boundary: string,
  serviceURL: string,
  authToken: string
): Promise<UploadPortalResponse> => {
  const blobSizeMB = blob.size / (1024 * 1024);
  const CHUNK_SIZE = 20 * 1024 * 1024; // 20MB chunks
  const DIRECT_UPLOAD_THRESHOLD = 20 * 1024 * 1024; // 20MB threshold for direct upload

  console.log(`Uploading DICOM file: ${blobSizeMB.toFixed(2)}MB`);

  // Strategy 1: Chunked Upload (>= 20MB) - Synchronous
  // Use chunked upload for all files >= 20MB to handle any size reliably
  if (blob.size >= DIRECT_UPLOAD_THRESHOLD) {
    const totalChunks = Math.ceil(blob.size / CHUNK_SIZE);
    console.log(`Strategy: Chunked Upload (file >= 20MB)`);
    console.log(`File will be split into ${totalChunks} chunks of ${(CHUNK_SIZE / 1024 / 1024).toFixed(0)}MB each`);
    const response = await uploadInChunks(blob, boundary, serviceURL, authToken, CHUNK_SIZE);
    return await parseResponse(response, blob.size);
  }

  // Strategy 2: Direct FormData Upload (< 20MB) - Synchronous
  console.log(`Strategy: Direct FormData Upload (file < 20MB)`);
  const formData = new FormData();
  formData.append('blob', blob, 'dicom-multipart.bin');
  formData.append('boundary', boundary);
  
  const response = await fetch(serviceURL, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
    body: formData,
  });

  return await parseResponse(response, blob.size);
};

/**
 * Helper function to create user-friendly error messages
 */
const createUserFriendlyErrorMessage = (status: number, errorText: string, blobSize?: number): string => {
  // Special handling for 413 Payload Too Large
  if (status === 413 && blobSize) {
    const fileSizeMB = (blobSize / (1024 * 1024)).toFixed(2);
    return `File is too large (${fileSizeMB}MB) for upload.`;
  }
  
  // Handle other status codes
  if (status === 409) {
    return 'This study already exists.';
  } else if (status === 400) {
    return 'Invalid upload request.';
  } else if (status === 401 || status === 403) {
    return 'Authentication failed. Please refresh and try again.';
  } else if (status >= 500) {
    return 'Server error occurred. Please try again later.';
  } else if (status === 0) {
    return 'Network error. Please check your connection and try again.';
  } else {
    // Try to extract meaningful error message from errorText
    let cleanError = errorText;
    try {
      const errorData = JSON.parse(errorText);
      if (errorData.statusText) {
        cleanError = errorData.statusText;
      } else if (errorData.error) {
        cleanError = errorData.error;
      } else if (errorData.message) {
        cleanError = errorData.message;
      }
    } catch (e) {
      // If not JSON, use the text as is but clean it up
      cleanError = errorText.replace(/^.*?\{/, '{').replace(/\}.*$/, '}');
      try {
        const errorData = JSON.parse(cleanError);
        if (errorData.statusText) {
          cleanError = errorData.statusText;
        }
      } catch (e2) {
        // If still not parseable, use original text
        cleanError = errorText;
      }
    }
    
    return `Upload failed: ${cleanError || 'Unknown error'}`;
  }
};

/**
 * Helper function to parse Response to UploadPortalResponse
 */
const parseResponse = async (response: Response, blobSize?: number): Promise<UploadPortalResponse> => {
  if (!response.ok) {
    let errorText = response.statusText;
    try {
      const errorData = await response.json();
      errorText = JSON.stringify(errorData);
    } catch (e) {
      try {
        errorText = await response.text();
      } catch (textError) {
        errorText = response.statusText;
      }
    }
    
    const userFriendlyMessage = createUserFriendlyErrorMessage(response.status, errorText, blobSize);
    
    return {
      ok: false,
      status: response.status,
      statusText: userFriendlyMessage,
      successfulFiles: [],
      failedFiles: [],
    };
  }

  const responseData = await response.json();
  return {
    ok: responseData.ok || false,
    status: responseData.status || response.status,
    statusText: responseData.statusText || response.statusText,
    successfulFiles: responseData.successfulFiles || [],
    failedFiles: responseData.failedFiles || [],
  };
};